/**
 * Скрытый режим проверки (05.10, ЛК на iPhone: «ниже первого экрана всё чёрное, хоть и скроллится»).
 *
 * Повторить ошибку можно только на телефоне владельца, поэтому подозреваемых проверяет он сам: адрес с ?diag=1
 * включает панель (components/DiagPanel), ?diag=0 или кнопка «Выключить» — выключает. Метка живёт до закрытия
 * вкладки (sessionStorage). Обычный посетитель режима не видит: без метки здесь только одно чтение
 * sessionStorage на старте, панель — отдельный ленивый кусок.
 *
 * Каждый переключатель убирает одного подозреваемого и ПЕРЕЗАГРУЖАЕТ страницу: проверка всегда с чистой
 * загрузки (картинки, раскодированные до переключения, иначе так и держали бы память). Какой переключатель
 * убирает чёрное — тот и причина.
 */

export const DIAG_FLAGS = [
	{ key: 'noMedia', label: 'Без картинок генераций', hint: 'кабинет не грузит картинки и видео' },
	{ key: 'noAmbient', label: 'Без фона', hint: 'светящиеся шары и подсветка за страницей' },
	{ key: 'noAnim', label: 'Без анимаций', hint: 'CSS-анимации и SVG-анимация баланса' },
	{ key: 'noFilters', label: 'Без размытий и теней', hint: 'filter, backdrop-filter, will-change' },
	{ key: 'noChrome', label: 'Без шапки и меню', hint: 'липкая шапка и боковое меню' },
	{ key: 'colorBg', label: 'Цветной фон страницы', hint: 'чёрное стало малиновым — Safari не дорисовал содержимое' },
] as const

export type DiagFlag = (typeof DIAG_FLAGS)[number]['key']
type DiagState = { on: boolean; flags: Partial<Record<DiagFlag, boolean>>; collapsed?: boolean }

const STORAGE_KEY = 'umnik.diag'
const STYLE_ID = 'umnik-diag-css'

/** Стили переключателей — по одному подозреваемому на флаг. */
const FLAG_CSS: Record<DiagFlag, string> = {
	noMedia: '.account-page img, .account-page video { display: none !important; }',
	noAmbient: '.app-ambient, .app-atmosphere, .app-orbs { display: none !important; }',
	noAnim: '*, *::before, *::after { animation: none !important; transition: none !important; }',
	noFilters:
		'*, *::before, *::after { filter: none !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; will-change: auto !important; }',
	noChrome: '.header, .sidebar, .sidebar-overlay { display: none !important; }',
	colorBg: 'html, body { background: #ff1fa8 !important; }',
}

/** Перезагрузка — через объект, чтобы тесты могли её подменить. */
export const diagEnv = {
	reload: () => window.location.reload(),
	replaceUrl: (url: string) => window.history.replaceState(window.history.state, '', url),
}

let state: DiagState = { on: false, flags: {} }

function read(): DiagState {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (raw) {
			const s = JSON.parse(raw) as DiagState
			if (s && s.on) return { on: true, flags: s.flags || {}, collapsed: !!s.collapsed }
		}
	} catch {
		/* приватный режим или битое значение — режим просто выключен */
	}
	return { on: false, flags: {} }
}

function write(s: DiagState) {
	try {
		if (s.on) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s))
		else sessionStorage.removeItem(STORAGE_KEY)
	} catch {
		/* без хранилища режим живёт до перезагрузки */
	}
}

export function isDiagOn(): boolean {
	return state.on
}

export function diagFlag(flag: DiagFlag): boolean {
	return state.on && !!state.flags[flag]
}

export function diagFlags(): Partial<Record<DiagFlag, boolean>> {
	return { ...state.flags }
}

/** Панель свёрнута в значок: после переключателя страница перезагружается и должна быть видна целиком. */
export function diagCollapsed(): boolean {
	return state.on && !!state.collapsed
}

export function setDiagCollapsed(collapsed: boolean): void {
	if (!state.on) return
	state = { ...state, collapsed }
	write(state)
}

/** SVG-анимации (SMIL, например «бесконечность» в карточке баланса) CSS не останавливает — ставим на паузу. */
function pauseSmil() {
	let queued = false
	const pause = () => {
		queued = false
		document.querySelectorAll('svg').forEach((svg) => {
			if (!svg.querySelector('animate, animateMotion, animateTransform, set')) return
			try {
				svg.pauseAnimations()
				svg.setCurrentTime(0)
			} catch {
				/* не корневой svg — его паузит внешний */
			}
		})
	}
	pause()
	// Таймер, а не rAF: во вкладке в фоне кадров нет, и SVG, добавленные React, оставались бы на ходу.
	new MutationObserver(() => {
		if (queued) return
		queued = true
		window.setTimeout(pause, 30)
	}).observe(document.documentElement, { childList: true, subtree: true })
}

/**
 * Вызывать один раз до createRoot: читает метку из адреса и ставит стили включённых переключателей до первого
 * кадра. Возвращает, включён ли режим.
 */
export function initDiag(search: string = window.location.search): boolean {
	const q = new URLSearchParams(search).get('diag')
	if (q === '0') write({ on: false, flags: {} })
	else if (q === '1' && !read().on) write({ on: true, flags: {} })
	state = read()
	document.getElementById(STYLE_ID)?.remove()
	if (!state.on) return false
	const css = DIAG_FLAGS.filter((f) => state.flags[f.key])
		.map((f) => FLAG_CSS[f.key])
		.join('\n')
	if (css) {
		const el = document.createElement('style')
		el.id = STYLE_ID
		el.textContent = css
		document.head.appendChild(el)
	}
	if (state.flags.noAnim) pauseSmil()
	return true
}

/** Переключить подозреваемого и перезагрузить страницу — проверка всегда с чистой загрузки. */
export function setDiagFlag(flag: DiagFlag, value: boolean): void {
	state = { on: true, flags: { ...state.flags, [flag]: value }, collapsed: true }
	write(state)
	diagEnv.reload()
}

/** Выключить режим: метку из адреса убрать, страницу перезагрузить без переключателей. */
export function diagOff(): void {
	state = { on: false, flags: {} }
	write(state)
	const url = new URL(window.location.href)
	url.searchParams.delete('diag')
	diagEnv.replaceUrl(url.pathname + url.search + url.hash)
	diagEnv.reload()
}
